"use client";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Ticket } from "lucide-react";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { apiService, Session } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

interface BookingTableProps {
  selectedGame: string;
  tableData: any[];
  onInputChange: (rowIndex: number, colIndex: number, value: string) => void;
  selectedGameTime?: string;
  loading?: boolean;
  activeSession?: Session | null;
}

interface BookingRow {
  id: string;
  item: string;
  from: string;
  to: string;
  qty: number;
  amt: number;
}

export function BookingTable({ selectedGame, tableData, onInputChange, selectedGameTime = "Morning (13:00)", loading = false, activeSession }: BookingTableProps) {
  const { toast } = useToast();
  const { user } = useAuth();

  const [selectedTicketType, setSelectedTicketType] = useState("Dear 6RS");

  const [bookingRows, setBookingRows] = useState<BookingRow[]>([
    {
      id: "1",
      item: "",
      from: "",
      to: "",
      qty: 0,
      amt: 0,
    },
  ]);

  const [animatingToFields, setAnimatingToFields] = useState<Set<string>>(new Set());
  const [modifiedRows, setModifiedRows] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const sequenceOptions = [3, 5, 10, 15, 20, 25, 30, 50];
  const ticketOptions = ["Dear 6RS", "Dear 10RS"];

  const getTimePrefix = () => {
    if (selectedGameTime.includes("Morning")) return "M";
    if (selectedGameTime.includes("Day")) return "D";
    if (selectedGameTime.includes("Evening")) return "E";
    return "M";
  };

  const getItemPurchaseLimit = (itemNumber: number) => Math.floor(50 / itemNumber);

  const getItemPurchaseCount = (itemNumber: number, excludeRowId?: string) => {
    const prefix = getTimePrefix();
    const targetItem = `${prefix}${itemNumber}`;
    return bookingRows.filter((row) => row.id !== excludeRowId && row.item === targetItem).length;
  };

  const getRemainingItemCapacity = (itemNumber: number, excludeRowId?: string) => {
    const maxLimit = getItemPurchaseLimit(itemNumber);
    const currentCount = getItemPurchaseCount(itemNumber, excludeRowId);
    return maxLimit - currentCount;
  };

  const getTotalItemValue = (excludeRowId?: string) => {
    return bookingRows
      .filter((row) => row.id !== excludeRowId && row.item !== "")
      .reduce((total, row) => {
        const itemNumber = Number.parseInt(row.item.replace(/[MDE]/, "")) || 0;
        return total + itemNumber;
      }, 0);
  };

  const isRangeOverlapping = (from: string, to: string, currentRowId: string) => {
    if (!from || !to) return { isOverlapping: false, message: "" };

    const fromNum = Number.parseInt(from);
    const toNum = Number.parseInt(to);

    if (isNaN(fromNum) || isNaN(toNum)) {
      return { isOverlapping: false, message: "" };
    }

    if (toNum < fromNum) {
      return { isOverlapping: false, message: "TO value cannot be less than FROM value." };
    }

    const exactDuplicateFrom = bookingRows.some((row) => row.id !== currentRowId && row.from === from && from !== "");
    const exactDuplicateTo = bookingRows.some((row) => row.id !== currentRowId && row.to === to && to !== "");

    if (exactDuplicateFrom && exactDuplicateTo) {
      return { isOverlapping: true, message: "Both FROM and TO values have already been used." };
    } else if (exactDuplicateFrom) {
      return { isOverlapping: true, message: "This FROM value has already been used." };
    } else if (exactDuplicateTo) {
      return { isOverlapping: true, message: "This TO value has already been used." };
    }

    for (const row of bookingRows) {
      if (row.id === currentRowId || !row.from || !row.to) continue;
      const existingFromNum = Number.parseInt(row.from);
      const existingToNum = Number.parseInt(row.to);
      if (isNaN(existingFromNum) || isNaN(existingToNum)) continue;

      const overlapStart = Math.max(fromNum, existingFromNum);
      const overlapEnd = Math.min(toNum, existingToNum);

      if (overlapStart <= overlapEnd) {
        return { isOverlapping: true, message: `Range ${from}-${to} overlaps with existing range.` };
      }
    }
    return { isOverlapping: false, message: "" };
  };

 const calculateQtyAndAmt = (from: string, to: string, ticketType: string, itemNumber: number) => {
    if (!from) return { qty: 0, amt: 0 };

    // If 'to' is empty, treat it as a single ticket (effectiveTo = from)
    const effectiveTo = to === "" ? from : to;

    const fromNum = Number.parseInt(from);
    const toNum = Number.parseInt(effectiveTo);

    if (isNaN(fromNum) || isNaN(toNum) || toNum < fromNum) return { qty: 0, amt: 0 };

    const qty = toNum - fromNum + 1;
    const ticketPrice = ticketType === "Dear 6RS" ? 6 : 10;
    const amt = qty * ticketPrice * itemNumber;

    return { qty, amt: Number.parseFloat(amt.toFixed(2)) };
  };

  const handleGlobalTicketChange = (newTicketType: string) => {
    setSelectedTicketType(newTicketType);

    setBookingRows((prev) =>
      prev.map((row) => {
        const itemNumber = Number.parseInt(row.item.replace(/[MDE]/, "")) || 0;
        const { qty, amt } = calculateQtyAndAmt(row.from, row.to, newTicketType, itemNumber);
        return { ...row, qty, amt };
      })
    );
  };

 const handleFromChange = (rowId: string, value: string) => {
    if (value.length > 5) return;

    setBookingRows((prev) =>
      prev.map((row) => {
        if (row.id === rowId) {
          const updatedRow = { ...row, from: value };
          const itemNumber = Number.parseInt(updatedRow.item.replace(/[MDE]/, "")) || 0;

          // Note: calculateQtyAndAmt handles the empty 'to' internally now
          const { qty, amt } = calculateQtyAndAmt(updatedRow.from, updatedRow.to, selectedTicketType, itemNumber);
          return { ...updatedRow, qty, amt };
        }
        return row;
      })
    );

    if (value.length === 5) {
      setTimeout(() => {
        const toField = document.getElementById(`to-${rowId}`);
        if (toField) toField.focus();
      }, 50);
    }
  };

  const handleToChange = (rowId: string, value: string) => {
    if (value.length > 5) return;

    setBookingRows((prev) =>
      prev.map((row) => {
        if (row.id === rowId) {
          const updatedRow = { ...row, to: value };
          const itemNumber = Number.parseInt(updatedRow.item.replace(/[MDE]/, "")) || 0;

          const { qty, amt } = calculateQtyAndAmt(updatedRow.from, updatedRow.to, selectedTicketType, itemNumber);
          return { ...updatedRow, qty, amt };
        }
        return row;
      })
    );

    if (value.length === 5) {
      const currentRow = bookingRows.find((row) => row.id === rowId);
      if (currentRow && currentRow.from) {
        const fromNum = Number.parseInt(currentRow.from);
        const toNum = Number.parseInt(value);
        if (!isNaN(fromNum) && !isNaN(toNum) && toNum >= fromNum) {
          setTimeout(() => {
            const qtyField = document.getElementById(`qty-${rowId}`);
            if (qtyField) qtyField.focus();
          }, 50);
        }
      }
    }
  };

  const handleToBlur = (rowId: string, value: string) => {
    const currentRow = bookingRows.find((row) => row.id === rowId);
    if (!currentRow || !currentRow.from || !value) return;

    const fromValue = currentRow.from;
    const fromLength = fromValue.length;
    const toValue = value.padStart(fromLength, "0");

    let finalToValue = fromValue;
    for (let i = 0; i < Math.min(value.length, fromLength); i++) {
      const position = fromLength - 1 - i;
      finalToValue = finalToValue.substring(0, position) + toValue[position] + finalToValue.substring(position + 1);
    }

    const fromNum = Number.parseInt(fromValue);
    const finalToNum = Number.parseInt(finalToValue);

    if (finalToNum < fromNum) {
      finalToValue = fromValue;
    }

    const overlapCheck = isRangeOverlapping(currentRow.from, finalToValue, rowId);
    if (overlapCheck.isOverlapping) {
      toast({
        title: "Invalid Range",
        description: overlapCheck.message,
        variant: "destructive",
      });
      return;
    }

    setBookingRows((prev) =>
      prev.map((row) => {
        if (row.id === rowId) {
          const updatedRow = { ...row, to: finalToValue };
          const itemNumber = Number.parseInt(updatedRow.item.replace(/[MDE]/, "")) || 0;
          const { qty, amt } = calculateQtyAndAmt(updatedRow.from, updatedRow.to, selectedTicketType, itemNumber);
          return { ...updatedRow, qty, amt };
        }
        return row;
      })
    );
  };

  const handleQtyChange = (rowId: string, value: string) => {
    const numValue = Number.parseInt(value) || 0;
    const finalQty = Math.max(numValue, 1);
    const currentRow = bookingRows.find((row) => row.id === rowId);

    if (currentRow && currentRow.from) {
      const fromNum = Number.parseInt(currentRow.from);
      const newToNum = fromNum + finalQty - 1;
      const maxValue = Math.pow(10, currentRow.from.length) - 1;
      const newToValue = Math.min(newToNum, maxValue).toString().padStart(currentRow.from.length, "0");

      const overlapCheck = isRangeOverlapping(currentRow.from, newToValue, rowId);
      if (overlapCheck.isOverlapping) {
        toast({
          title: "Cannot adjust TO value",
          description: "The calculated TO value would create overlapping ranges.",
          variant: "destructive",
        });
        return;
      }

      if (currentRow.to !== newToValue) {
        setModifiedRows((prev) => new Set(prev).add(rowId));
        setAnimatingToFields((prev) => new Set(prev).add(rowId));
        setTimeout(() => {
          setAnimatingToFields((prev) => {
            const newSet = new Set(prev);
            newSet.delete(rowId);
            return newSet;
          });
        }, 600);
      }

      setBookingRows((prev) =>
        prev.map((row) => {
          if (row.id === rowId) {
            const ticketPrice = selectedTicketType === "Dear 6RS" ? 6 : 10;
            const itemNumber = Number.parseInt(row.item.replace(/[MDE]/, "")) || 0;
            const amt = finalQty * ticketPrice * itemNumber;
            return { ...row, qty: finalQty, amt: Number.parseFloat(amt.toFixed(2)), to: newToValue };
          }
          return row;
        })
      );
    } else {
      setBookingRows((prev) =>
        prev.map((row) => {
          if (row.id === rowId) {
            const ticketPrice = selectedTicketType === "Dear 6RS" ? 6 : 10;
            const itemNumber = Number.parseInt(row.item.replace(/[MDE]/, "")) || 0;
            const amt = finalQty * ticketPrice * itemNumber;
            return { ...row, qty: finalQty, amt: Number.parseFloat(amt.toFixed(2)) };
          }
          return row;
        })
      );
    }
  };

  const handleAmtChange = (rowId: string, value: string) => {
    const numValue = Number.parseFloat(value) || 0;

    setBookingRows((prev) =>
      prev.map((row) => {
        if (row.id === rowId) {
          const ticketPrice = selectedTicketType === "Dear 6RS" ? 6 : 10;
          const itemNumber = Number.parseInt(row.item.replace(/[MDE]/, "")) || 1;
          const qty = Math.round(numValue / (ticketPrice * itemNumber));
          return { ...row, amt: numValue, qty: Math.max(qty, 1) };
        }
        return row;
      })
    );
  };

  const handleItemChange = (rowId: string, sequence: number) => {
    const prefix = getTimePrefix();
    const item = `${prefix}${sequence}`;
    const currentItemCount = getItemPurchaseCount(sequence, rowId);
    const maxItemLimit = getItemPurchaseLimit(sequence);
    const totalItemValue = getTotalItemValue(rowId);

    if (currentItemCount >= maxItemLimit) {
      toast({
        title: "Item Purchase Limit Exceeded",
        description: `${item} can only be purchased ${maxItemLimit} times maximum.`,
        variant: "destructive",
      });
      return;
    }

    if (totalItemValue + sequence > 50) {
      const remainingCapacity = 50 - totalItemValue;
      toast({
        title: "Total Item Value Limit Exceeded",
        description: `Adding ${item} would exceed the total limit of 50. You have ${remainingCapacity} remaining capacity.`,
        variant: "destructive",
      });
      return;
    }

    setBookingRows((prev) =>
      prev.map((row) => {
        if (row.id === rowId) {
          const updatedRow = { ...row, item };
          // Use selectedTicketType
          const { qty, amt } = calculateQtyAndAmt(updatedRow.from, updatedRow.to, selectedTicketType, sequence);
          return { ...updatedRow, qty, amt };
        }
        return row;
      })
    );

    setTimeout(() => {
      const fromField = document.getElementById(`from-${rowId}`);
      if (fromField) fromField.focus();
    }, 50);
  };

 const addNewRow = () => {
    setBookingRows((prev) => {
      const newRows = [...prev];
      const lastRowIndex = newRows.length - 1;
      let lastItem = "";

      if (lastRowIndex >= 0) {
        // Copy the previous row's data
        const lastRow = { ...newRows[lastRowIndex] };
        
        // Lock in 'to' = 'from' if it was left empty
        if (lastRow.from && lastRow.to === "") {
          lastRow.to = lastRow.from;
        }
        
        newRows[lastRowIndex] = lastRow;
        
        // GRAB THE ITEM HERE
        lastItem = lastRow.item; 
      }

      const newRow: BookingRow = {
        id: Date.now().toString(),
        item: lastItem, // SET THE ITEM HERE
        from: "",
        to: "",
        qty: 0,
        amt: 0,
      };

      newRows.push(newRow);
      return newRows;
    });
  };

  const removeRow = (rowId: string) => {
    if (bookingRows.length > 1) {
      setBookingRows((prev) => prev.filter((row) => row.id !== rowId));
      setModifiedRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(rowId);
        return newSet;
      });
    }
  };

  const handleChoiceRangeBooking = async () => {
    if (!activeSession) {
      toast({ title: "No Active Session", description: "Please wait for an active session.", variant: "destructive" });
      return;
    }

    if (!user) {
      toast({ title: "Authentication Required", description: "Please login to place bets.", variant: "destructive" });
      return;
    }

    setSubmitting(true);

    try {
    
      const validRows = bookingRows
       
        .map(row => {
            if(row.to === "") return { ...row, to: row.from };
            return row;
        })
        .filter((row) => {
         
          if (!row.item || !row.from || !row.to) return false; 
          const fromNum = Number.parseInt(row.from);
          const toNum = Number.parseInt(row.to);
          if (isNaN(fromNum) || isNaN(toNum) || toNum < fromNum) return false;
          
          return row.qty > 0 && row.amt > 0;
        });

      if (validRows.length === 0) {
        toast({ title: "No Valid Bets", description: "Please fill in at least one valid betting row.", variant: "destructive" });
        setSubmitting(false);
        return;
      }

      let successCount = 0;
      let errorCount = 0;

      for (const row of validRows) {
        try {
          const response = await apiService.createChoiceRangeBooking({
            sessionId: activeSession.id,
            item: row.item,
            from: row.from,
            to: row.to,
            tickets: selectedTicketType,
            qty: row.qty,
            amount: row.amt,
          });

          if (response.success) successCount++;
          else errorCount++;
        } catch (error) {
          errorCount++;
        }
      }

      if (errorCount === 0) {
        toast({ title: "Success", description: `All ${successCount} choice range bets placed successfully!`, variant: "default" });
        setBookingRows([
          {
            id: Date.now().toString(),
            
            item: validRows[validRows.length - 1]?.item || "", 
            from: "",
            to: "",
            qty: 0,
            amt: 0,
          },
        ]);
      } else {
        toast({ title: "Partial Success", description: `${successCount} bets placed, ${errorCount} failed.`, variant: successCount > 0 ? "default" : "destructive" });
      }
    } catch (error) {
      console.error("Error placing bets:", error);
    } finally {
      setSubmitting(false);
    }
  };

useEffect(() => {
    const lastRow = bookingRows[bookingRows.length - 1];
    
    // Logic: 
    // 1. Must have Item and From
    // 2. Qty must be calculated (>0)
    // 3. From must be fully typed (5 chars)
    // 4. We do NOT check if lastRow.to is populated, allowing single tickets to auto-add
    if (lastRow && lastRow.item && lastRow.from && lastRow.qty > 0 && lastRow.from.length === 5) {
      addNewRow();
    }
  }, [bookingRows]);

  const getTableTitle = () => {
    switch (selectedGame) {
      case "Dear Single":
        return "Game Time (No active session)";
      case "Rajshree Punjab":
        return "Rajshree Punjab Game";
      case "Booking - Choice":
        return "Booking/Choice Interface";
      default:
        return "Game Time (No active session)";
    }
  };

  // UPDATE THIS FUNCTION
  const getAvailableSequenceOptions = (currentRowId: string) => {
    // We removed the logic that filtered out 'chosenItems'.
    // Now you can select the same ticket multiple times (up to the purchase limit).

    return sequenceOptions.filter((seq) => {
      // Check how many times this specific item (e.g., "5") has been used across all rows
      const currentCount = getItemPurchaseCount(seq, currentRowId);
      
      // Calculate max allowed rows for this item (e.g., 50 / 5 = 10 rows allowed)
      const maxLimit = getItemPurchaseLimit(seq);
      
      // Calculate total value limit
      const totalItemValue = getTotalItemValue(currentRowId);
      
      // Allow the item if we haven't hit the row limit AND total value limit
      return currentCount < maxLimit && totalItemValue + seq <= 50;
    });
  };

const isRowValid = (row: BookingRow) => {
 
    if (!row.item || !row.from) return false;
    
   
    const effectiveTo = row.to === "" ? row.from : row.to;

    const fromNum = Number.parseInt(row.from);
    const toNum = Number.parseInt(effectiveTo);
    
  
    if (isNaN(fromNum) || isNaN(toNum) || toNum < fromNum) return false;
    
  
    const itemNumber = Number.parseInt(row.item.replace(/[MDE]/, "")) || 0;
    if (itemNumber === 0) return false;
    
  
    const overlapCheck = isRangeOverlapping(row.from, effectiveTo, row.id);
    if (overlapCheck.isOverlapping) return false;
    
   
    return row.qty > 0 && row.amt > 0;
  };

  const isFormValid = () => {
    const lastRow = bookingRows[bookingRows.length - 1];
    if (!lastRow) return false;
    return isRowValid(lastRow);
  };

  const renderChoiceBookingButton = () => {
    if (selectedGame !== "Booking - Choice") return null;
    const totalAmount = bookingRows.reduce((sum, row) => sum + row.amt, 0);
    const validRows = bookingRows.filter((row) => isRowValid(row)).length;

    return (
      <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
          <div className="text-sm">
            <div className="font-semibold text-green-800">
              Ready to place {validRows} bet{validRows !== 1 ? "s" : ""}
            </div>
            <div className="text-green-600">Total Amount: ₹{totalAmount.toFixed(2)}</div>
          </div>
          <Button onClick={handleChoiceRangeBooking} disabled={submitting || validRows === 0 || !activeSession} className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 w-full sm:w-auto">
            {submitting ? "Placing Bets..." : `Place Bets (${validRows})`}
          </Button>
        </div>
      </div>
    );
  };

  if (selectedGame === "Booking - Choice") {
    const totalQuantity = bookingRows.reduce((sum, row) => sum + row.qty, 0);
    const totalAmount = bookingRows.reduce((sum, row) => sum + row.amt, 0);

    return (
      <Card className="border-none shadow-none">
        <CardHeader className="bg-gradient-to-r from-purple-700 to-purple-800 text-white rounded-t-lg py-3 px-4">
          <CardTitle className="text-lg font-semibold text-center">Booking/Choice no.</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-100 shadow-sm">
            <div className="flex flex-col sm:flex-row gap-4 items-end sm:items-center">
              <div className="w-full sm:w-1/2">
                <label className="block text-sm font-semibold text-purple-900 mb-1">
                  <Ticket className="w-4 h-4 inline mr-1 mb-1" /> Select Ticket Type
                </label>
                <select value={selectedTicketType} onChange={(e) => handleGlobalTicketChange(e.target.value)} className="w-full px-3 py-2 text-sm border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white">
                  {ticketOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-full sm:w-1/2 text-xs text-purple-700">
                <p>
                  Current Rate: <strong>{selectedTicketType === "Dear 6RS" ? "₹6.00" : "₹10.00"}</strong> per qty × Item Value
                </p>
                <p>Tickets & Prices apply to all rows below.</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {bookingRows.map((row, index) => {
              const isAnimating = animatingToFields.has(row.id);
              const isModified = modifiedRows.has(row.id);
              const isRowComplete = isRowValid(row);

              return (
                <div key={row.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200 shadow-sm">
                  <div className="grid grid-cols-5 gap-2 mb-2">
                    <div className="col-span-1">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Item</label>
                      <select value={row.item.replace(/[MDE]/, "")} onChange={(e) => handleItemChange(row.id, Number.parseInt(e.target.value))} className="w-full px-1 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500">
                        <option value="">Sel</option>
                        {getAvailableSequenceOptions(row.id).map((seq) => (
                          <option key={seq} value={seq}>
                            {getTimePrefix()}
                            {seq}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-span-1">
                      <label className="block text-xs font-medium text-gray-700 mb-1">From</label>
                      <Input id={`from-${row.id}`} type="text" maxLength={5} value={row.from} onChange={(e) => handleFromChange(row.id, e.target.value.replace(/\D/g, ""))} placeholder="00" className="w-full text-sm border-gray-300 text-gray-900 focus:border-purple-500 focus:ring-purple-500 px-2" />
                    </div>

                    <div className="col-span-1">
                      <label className={cn("block text-xs font-medium mb-1", isAnimating || isModified ? "text-blue-600" : "text-gray-700")}>To</label>
                      <Input id={`to-${row.id}`} type="text" maxLength={5} value={row.to} onChange={(e) => handleToChange(row.id, e.target.value.replace(/\D/g, ""))} onBlur={(e) => handleToBlur(row.id, e.target.value.replace(/\D/g, ""))} placeholder="00" className={cn("w-full text-sm px-2", isAnimating || isModified ? "border-blue-500 text-blue-700 bg-blue-50" : "border-gray-300")} />
                    </div>

                    <div className="col-span-1">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Qty</label>
                      <Input id={`qty-${row.id}`} type="number" min="1" value={row.qty || ""} onChange={(e) => handleQtyChange(row.id, e.target.value)} className="w-full text-sm bg-green-50 font-semibold px-2" placeholder="1" />
                    </div>

                    <div className="col-span-1">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Amt</label>
                      <Input id={`amt-${row.id}`} type="number" min="0" step="0.01" value={row.amt || ""} onChange={(e) => handleAmtChange(row.id, e.target.value)} className="w-full text-sm bg-blue-50 font-semibold px-2" placeholder="0.00" />
                    </div>
                  </div>

                  <div className="flex gap-2 mt-2">
                    {index === bookingRows.length - 1 && (
                      <Button type="button" onClick={addNewRow} disabled={!isFormValid()} className={cn("flex-1 h-8 text-xs", isRowComplete ? "bg-green-600 hover:bg-green-700 text-white" : "bg-gray-300 text-gray-500")}>
                        <Plus className="h-3 w-3 mr-1" /> Add
                      </Button>
                    )}
                    {bookingRows.length > 1 && (
                      <Button type="button" variant="destructive" onClick={() => removeRow(row.id)} className="flex-1 h-8 text-xs">
                        <Trash2 className="h-3 w-3 mr-1" /> Del
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}

            <div className="mt-3 p-3 bg-blue-50 rounded-lg flex justify-between items-center border border-blue-100">
              <div>
                <span className="text-xs text-gray-500 block">Total Quantity</span>
                <span className="font-bold text-lg text-purple-700">{totalQuantity}</span>
              </div>
              <div className="text-right">
                <span className="text-xs text-gray-500 block">Total Amount</span>
                <span className="font-bold text-xl text-green-700">₹{totalAmount.toFixed(2)}</span>
              </div>
            </div>

            {renderChoiceBookingButton()}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-none shadow-none">
      <CardHeader className="bg-gradient-to-r from-red-700 to-red-800 text-white rounded-t-lg py-3 px-4">
        <CardTitle className="text-lg font-semibold text-center">{getTableTitle()}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table className="min-w-max">
            <TableHeader>
              <TableRow className="bg-red-600 hover:bg-red-600 text-white">
                <TableHead className="w-[120px] text-white py-1 px-2 sm:px-3">Group</TableHead>
                {Array.from({ length: 10 }, (_, i) => (
                  <TableHead key={i} className="text-center text-white py-1 px-1 sm:px-2">
                    {i}
                  </TableHead>
                ))}
                <TableHead className="text-center text-white py-1 px-2 sm:px-3">Qty</TableHead>
                <TableHead className="text-center text-white py-1 px-2 sm:px-3">Amt.</TableHead>
                <TableHead className="text-center text-white py-1 px-2 sm:px-3">Result (13:00)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tableData.map((row, rowIndex) => (
                <TableRow key={row.group} className={cn(rowIndex % 2 === 0 ? "bg-blue-50" : "bg-green-50", "hover:bg-opacity-80")}>
                  <TableCell className="font-medium text-gray-800 py-1 px-2 sm:px-3">{row.group}</TableCell>
                  {Array.from({ length: 10 }, (_, colIndex) => (
                    <TableCell key={colIndex} className="text-center py-1 px-1 sm:px-2">
                      <Input
                        type="text"
                        maxLength={1}
                        value={row.inputs[colIndex] || ""}
                        onChange={(e) => {
                          onInputChange(rowIndex, colIndex, e.target.value);
                          if (e.target.value && colIndex < 9) {
                            setTimeout(() => {
                              const nextCell = document.querySelector(`[data-row="${rowIndex}"][data-col="${colIndex + 1}"] input`);
                              if (nextCell) (nextCell as HTMLElement).focus();
                            }, 50);
                          }
                        }}
                        data-row={rowIndex}
                        data-col={colIndex}
                        className="w-8 h-6 sm:w-9 sm:h-7 text-center border border-gray-300 rounded-md focus:border-purple-500 focus:ring-purple-500"
                      />
                    </TableCell>
                  ))}
                  <TableCell className="text-center text-gray-600 py-1 px-2 sm:px-3">{row.qty}</TableCell>
                  <TableCell className="text-center text-gray-600 py-1 px-2 sm:px-3">{row.amt}</TableCell>
                  <TableCell className="text-center font-bold text-gray-800 py-1 px-2 sm:px-3">{row.result}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
